import styled, { css } from "styled-components";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { JobPreview } from "./JobPreview";
import { MissingSalaryWarning } from "./MissingSalaryWarning";

import { Button, Text } from "@otta/design";
import { modularScale, palette, pxToRem } from "@otta/design-tokens";
import { Feature, JobPreviewFragment } from "@hire/schema";
import { useAuthorization } from "@hire/providers/authorization";
import { Icon } from "@otta/icons";

type SectionType = "published" | "paused";

interface CoreJobListSectionProps {
  jobs: JobPreviewFragment[];
  review: boolean;
  type: SectionType;
}

export interface CoreJobListProps {
  jobs: JobPreviewFragment[];
  review: boolean;
}

const JobsListContainer = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: ${modularScale(1)};
  gap: ${modularScale()};
`;

const JobSectionDetails = {
  published: {
    title: "Published jobs",
    subTitle: "You do not have any published jobs on Otta.",
  },
  paused: {
    title: "Frozen jobs",
    subTitle:
      "Frozen jobs are not shown to candidates. You're missing out on applications.",
  },
};

const JobList = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${modularScale()};
  margin: ${pxToRem(16)} 0;
  flex-wrap: wrap;
  align-items: stretch;
`;

const JobSectionContainer = styled.div<{ type: SectionType }>`
  border-radius: 1rem;
  min-width: fit-content;
  padding: 1.5rem;

  ${({ type }) =>
    type === "published"
      ? css`
          background-color: ${palette.grayscale.shade50};
        `
      : type === "paused" &&
        css`
          background-color: ${palette.extended.blue.shade100};
          background-image: url("https://storage.googleapis.com/otta-static/images/hire-app/compressed-rotated-ice-cubes.png");
          background-repeat: no-repeat;
          background-position: top -119px right -68.607px;
        `}
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
`;

export function CoreJobList({ jobs, review }: CoreJobListProps) {
  const { message } = useAuthorization(Feature.JobSlots);

  return (
    <JobsListContainer>
      <JobSection type="published" jobs={jobs} review={review} />
      {(message === "PURCHASED" || message === "ACTIVE") && (
        <JobSection type="paused" jobs={jobs} review={review} />
      )}
    </JobsListContainer>
  );
}

const JobSection = ({ type, jobs, review }: CoreJobListSectionProps) => {
  const relevantJobs = useMemo(() => {
    return jobs.filter(job => {
      if (type === "published") {
        return job.isPaused === false;
      } else {
        return job.isPaused === true;
      }
    });
  }, [jobs, type]);

  const showSection =
    type === "published" || (type === "paused" && relevantJobs.length > 0);

  const showSubTitle =
    type === "paused" || (type === "published" && !relevantJobs.length);

  const showMissingSalaryWarning =
    type === "published" &&
    !review &&
    relevantJobs.some(job => !job.salaryRange);

  if (showSection) {
    return (
      <JobSectionContainer type={type}>
        <TitleSection>
          <Text data-testid={`job-section-${type}`} size={3} bold>
            {JobSectionDetails[type].title}
          </Text>
          {type === "published" && relevantJobs.length > 0 && (
            <Button
              level="secondary"
              as={Link}
              to="choose-job-slots"
              size="small"
            >
              <Icon
                icon="settings"
                style={{ paddingRight: modularScale(-8) }}
              />
              Swap published jobs
            </Button>
          )}
        </TitleSection>

        {showSubTitle && (
          <Text style={{ marginTop: pxToRem(8) }}>
            {JobSectionDetails[type].subTitle}
          </Text>
        )}

        {showMissingSalaryWarning && <MissingSalaryWarning />}

        <JobList>
          {relevantJobs.map(j => (
            <JobPreview
              key={j.id}
              {...j}
              isPublished={type === "published"}
              review={review}
            />
          ))}
        </JobList>
      </JobSectionContainer>
    );
  }

  return null;
};
